import { PillItem } from './pills-overflow.component';

export function stringsToPillItems(strings: string[]): PillItem[] {
  return strings.map((str) => ({
    id: str,
    data: str
  }));
}

export function keyValueToPillItems(entries: [string, string | number][]): PillItem[] {
  return entries.map(([key, value]) => ({
    id: key,
    data: { key, value }
  }));
}

/** Geometry of a single pill relative to the viewport (from getBoundingClientRect). */
export interface PillRect {
  top: number;
  bottom: number;
  right: number;
}

export interface VisibleCountParams {
  maxHeight: number;
  containerWidth: number;
  containerLeft: number;
  containerTop: number;
  pillHeight: number;
  gapSize: number;
  /** Horizontal space reserved for the "+N" counter. Defaults to 50. */
  counterSpace?: number;
}

/**
 * Pure geometry decision: given already-measured pill rects, return how many
 * pills fit before the overflow counter is needed. Kept side-effect free so all
 * DOM reads can be batched by the caller in a single pass (avoiding the
 * read-in-a-loop layout thrashing that measuring per pill would cause).
 */
export function computeVisibleCount(rects: PillRect[], params: VisibleCountParams): number {
  const counterSpace = params.counterSpace ?? 50;
  let visibleCount = 0;
  let needsCounter = false;

  for (let i = 0; i < rects.length; i++) {
    const rect = rects[i];
    const relativeTop = rect.top - params.containerTop;
    const relativeBottom = relativeTop + (rect.bottom - rect.top);

    if (relativeBottom > params.maxHeight) {
      needsCounter = true;
      break;
    }

    const isOnLastPossibleLine =
      relativeTop >= params.maxHeight - params.pillHeight - params.gapSize;
    if (isOnLastPossibleLine && i < rects.length - 1) {
      const pillRight = rect.right - params.containerLeft;
      if (pillRight + counterSpace + params.gapSize > params.containerWidth) {
        needsCounter = true;
        break;
      }
    }

    visibleCount = i + 1;
  }

  if (needsCounter && visibleCount === rects.length) {
    needsCounter = false;
  }

  if (needsCounter && visibleCount > 0) {
    visibleCount = Math.max(1, visibleCount - 1);
  }

  return visibleCount;
}
